import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import ErpDataGrid from '../../components/common/ErpDataGrid';
import { Add, Edit, Delete, Contacts, Refresh, Visibility } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { usePermissions } from '../../context/AuthContext';

const emptyForm = {
  account_name: '',
  company_name: '',
  company_address: '',
  website: '',
  office_direct_number: '',
  notes: ''
};

const AccountsList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = usePermissions();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const didOpenEditRef = useRef(false);

  useEffect(() => {
    const editId = location.state?.editAccountId;
    if (!editId || didOpenEditRef.current || rows.length === 0) return;
    const row = rows.find((r) => r.id === editId);
    if (row) {
      setEditing(row);
      setForm({ ...emptyForm, ...row });
      setOpen(true);
      didOpenEditRef.current = true;
      navigate('/accounts', { replace: true, state: {} });
    }
  }, [location.state?.editAccountId, rows, navigate]);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/accounts', { params: { page: 1, limit: 200, search } });
      const raw = response?.data?.data;
      const list = Array.isArray(raw) ? raw : [];
      setRows(list.map((r) => ({
        ...r,
        id: r?.id ?? r?.account_code ?? '',
        contacts: Array.isArray(r?.contacts) ? r.contacts : [],
        tenants: Array.isArray(r?.tenants) ? r.tenants : []
      })));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load accounts');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, [search]);

  const onSave = async () => {
    try {
      if (editing) {
        await api.put(`/accounts/${editing.id}`, form);
        toast.success('Account updated');
      } else {
        await api.post('/accounts', form);
        toast.success('Account created');
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyForm);
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save account');
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Delete account "${row.account_name}"?`)) return;
    try {
      await api.delete(`/accounts/${row.id}`);
      toast.success('Account deleted');
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete account');
    }
  };

  const columns = [
    { field: 'account_code', headerName: 'Account ID', width: 260, minWidth: 240 },
    { field: 'account_name', headerName: 'Account', flex: 1, minWidth: 180 },
    { field: 'company_name', headerName: 'Company', flex: 1, minWidth: 180 },
    { field: 'website', headerName: 'Website', flex: 1, minWidth: 200 },
    {
      field: 'contactsCount',
      headerName: 'Contacts',
      width: 110,
      sortable: false,
      renderCell: (params) => (Array.isArray(params?.row?.contacts) ? params.row.contacts.length : 0)
    },
    {
      field: 'tenantsCount',
      headerName: 'Tenants',
      width: 110,
      sortable: false,
      renderCell: (params) => (Array.isArray(params?.row?.tenants) ? params.row.tenants.length : 0)
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 220,
      sortable: false,
      renderCell: (params) => {
        const row = params?.row;
        if (!row?.id) return null;
        return (
          <Box sx={{ display: 'flex', gap: 0 }}>
            {can('accounts', 'view') && (
            <Tooltip title="View">
              <IconButton size="small" onClick={() => navigate(`/accounts/${row.id}`)}>
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
            )}
            {can('accounts', 'view') && (
            <Tooltip title="Contacts">
              <IconButton size="small" onClick={() => navigate(`/accounts/${row.id}/contacts`)}>
                <Contacts fontSize="small" />
              </IconButton>
            </Tooltip>
            )}
            {can('accounts', 'edit') && (
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => { setEditing(row); setForm({ ...emptyForm, ...row }); setOpen(true); }}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            )}
            {can('accounts', 'delete') && (
            <Tooltip title="Delete">
              <IconButton size="small" onClick={() => onDelete(row)}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
            )}
          </Box>
        );
      }
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Accounts</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={fetchAccounts}><Refresh /></IconButton>
          {can('accounts', 'add') && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => { setEditing(null); setForm(emptyForm); setOpen(true); }}
          >
            Add Account
          </Button>
          )}
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Search accounts"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Paper>

      <Paper sx={{ height: 620 }}>
        <ErpDataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          getRowId={(row) => row?.id ?? ''}
        />
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Account' : 'Add Account'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
            <TextField label="Account Name" value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} required />
            <TextField label="Company Name" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
            <TextField label="Company Address" value={form.company_address} onChange={(e) => setForm({ ...form, company_address: e.target.value })} multiline rows={2} />
            <TextField label="Website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
            <TextField label="Office Direct Number" value={form.office_direct_number} onChange={(e) => setForm({ ...form, office_direct_number: e.target.value })} />
            <TextField label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} multiline rows={3} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={onSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountsList;

